import type { JobLockData } from './types';

/**
 * Lock Helper Utilities
 * 
 * Centralized functions for lock-related operations.
 * Use these helpers everywhere to ensure consistent behavior.
 */

/**
 * Check if a job is locked
 * 
 * @param lockData - Lock data from job object
 * @returns true if job is locked, false otherwise
 */
export const isJobLocked = (lockData: JobLockData | null | undefined): boolean => {
  return lockData?.is_locked === true;
};

/**
 * Get lock reason for display
 * 
 * @param lockData - Lock data from job object
 * @returns Lock reason or empty string if not locked
 */
export const getLockReason = (lockData: JobLockData | null | undefined): string => {
  if (!isJobLocked(lockData)) {
    return '';
  }
  return lockData?.reason || 'No reason provided';
};

/**
 * Get lock metadata for display
 * 
 * @param lockData - Lock data from job object
 * @returns Formatted lock metadata or null if not locked
 */
export const getLockMetadata = (lockData: JobLockData | null | undefined) => {
  if (!isJobLocked(lockData)) {
    return null;
  }
  
  return {
    reason: lockData?.reason || 'No reason provided',
    lockedBy: lockData?.locked_by || 'Unknown',
    lockedAt: lockData?.locked_at 
      ? new Date(lockData.locked_at).toLocaleString() 
      : 'Unknown',
  };
};

/**
 * Format lock tooltip text
 * 
 * @param lockData - Lock data from job object
 * @returns Formatted tooltip string for hover display
 */
export const formatLockTooltip = (lockData: JobLockData | null | undefined): string => {
  const meta = getLockMetadata(lockData);
  if (!meta) return '';
  
  return `🔒 Date Locked (time reordering allowed)\n\n` +
         `Reason: ${meta.reason}\n` +
         `Locked by: ${meta.lockedBy}\n` +
         `Locked at: ${meta.lockedAt}\n\n` +
         `💡 You can still change the time to reorder on the same day.`;
};

/**
 * Check if user can modify dates
 * 
 * @param lockData - Lock data from job object
 * @returns true if dates can be modified, false if locked
 */
export const canModifyDates = (lockData: JobLockData | null | undefined): boolean => {
  return !isJobLocked(lockData);
};



